import {
  Badge,
  Button,
  EmptyState,
  ManageableItemsList,
  Modal,
  Text,
  Tooltip,
} from '@payaca/ambrosia';
import {
  automationActionTypeReadableNameMap,
  automationTriggerEventReadableNameMap,
} from '@payaca/helpers/automationHelper';
import {
  type AutomationActionType,
  automationActionTypeMap,
  automationEntityTypeMap,
  automationTriggerEventMap,
} from '@payaca/gql-helpers';
import { DateFormats } from '@payaca/helpers/internationalHelper';
import { AutomationsPermissions } from '@payaca/permissions/automations/automations.permissions';
import { SortDirection } from '@payaca/types/listViewTypes';
import UntitledIcon from '@payaca/untitled-icons';
import { format } from 'date-fns';
import { FC, PropsWithChildren, useState } from 'react';
import { useHistory } from 'react-router';

import useCreateAutomation from '@/api/mutations/automations/useCreateAutomation';
import useDeleteAutomation from '@/api/mutations/automations/useDeleteAutomation';
import useGetAutomations from '@/api/queries/automations/useGetAutomations';
import { AutomationsQuery } from '@/gql/graphql';
import { useAutomationDescriptions } from '@/hooks/useAutomationDescriptions';
import { useUserHasPermission } from '@/hooks/usePermissions';
import usePreferredDateFormat from '@/hooks/usePreferredDateFormat';
import { useTranslation } from '@/i18n';
import { PermissionGuard } from '@/ui/components/permissionGuard/PermissionGuard';
import {
  AUTOMATIONS_PAGE_SIZE,
  AutomationsSortableField,
  useAutomationsListState,
} from './useAutomationsListState';

const triggerEventOptions = automationTriggerEventMap.gqlValues.map(
  (triggerEvent) => {
    const internalTriggerEvent =
      automationTriggerEventMap.fromGql[triggerEvent];
    return {
      value: triggerEvent,
      label:
        automationTriggerEventReadableNameMap[internalTriggerEvent] ??
        internalTriggerEvent,
    };
  }
);

const getActionTypeLabel = (actionType: AutomationActionType) =>
  automationActionTypeReadableNameMap[
    automationActionTypeMap.fromGql[actionType]
  ] ?? actionType;

const actionTypeOptions = automationActionTypeMap.gqlValues.map(
  (actionType) => ({
    value: actionType,
    label: getActionTypeLabel(actionType),
  })
);

type AutomationRow = AutomationsQuery['automations']['items'][number];

const getEditAutomationPath = (privateId: AutomationRow['privateId']) =>
  `/automations/${privateId}?from=automations-rebuild`;

const AutomationsTab: FC = () => {
  const history = useHistory();
  const translate = useTranslation();
  const [automationToDelete, setAutomationToDelete] = useState<AutomationRow>();
  const {
    entityTypes,
    triggerEvents,
    actionTypes,
    sortBy,
    sortDirection,
    pageNumber,
    input,
    pagination,
    isFiltered,
    applySearch,
    setFilters,
    setSort,
    setPageNumber,
  } = useAutomationsListState();

  const { automations, isLoading, isPlaceholderData } = useGetAutomations({
    input,
    pagination,
  });

  const entityTypeOptions = automationEntityTypeMap.gqlValues
    .map((value) => ({
      value,
      label: translate(`automations.entityTypes.${value}`),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const canDelete = useUserHasPermission({
    permissions: [AutomationsPermissions.DELETE_AUTOMATION],
  });

  const { mutate: deleteAutomation, isPending: isDeleting } =
    useDeleteAutomation({
      onSuccess: () => {
        setAutomationToDelete(undefined);
        if (automations?.items.length === 1 && pageNumber > 1) {
          setPageNumber(pageNumber - 1);
        }
      },
    });

  const hasNoAutomations =
    !isLoading &&
    !isPlaceholderData &&
    !isFiltered &&
    automations?.totalCount === 0;

  return (
    <>
      <ManageableItemsList>
        <ManageableItemsList.HeaderBar
          heading="Automations"
          buttons={<CreateAutomationButton />}
        />

        {!hasNoAutomations && (
          <ManageableItemsList.ActionBar>
            <ManageableItemsList.ActionBar.SearchInput
              onChangeTimeout={applySearch}
              onBlur={(event) => applySearch(event.target.value)}
              changeTimeoutMs={300}
            />
            <ManageableItemsList.ActionBar.BasicFilter
              name="Type"
              options={entityTypeOptions}
              value={entityTypes}
              onChange={(value) => setFilters({ entityTypes: value })}
            />
            <ManageableItemsList.ActionBar.BasicFilter
              name="Trigger event"
              options={triggerEventOptions}
              value={triggerEvents}
              onChange={(value) => setFilters({ triggerEvents: value })}
            />
            <ManageableItemsList.ActionBar.BasicFilter
              name="Action types"
              options={actionTypeOptions}
              value={actionTypes}
              onChange={(value) => setFilters({ actionTypes: value })}
            />
          </ManageableItemsList.ActionBar>
        )}

        {hasNoAutomations ? (
          <EmptyState text="You don't have any automations yet." />
        ) : (
          <>
            <AutomationsTable
              automations={automations?.items ?? []}
              isLoading={
                isLoading || (isPlaceholderData && !automations?.items.length)
              }
              isFiltered={isFiltered}
              sortBy={sortBy}
              sortDirection={sortDirection}
              onSort={setSort}
              onClickRow={(row) =>
                history.push(getEditAutomationPath(row.privateId))
              }
              onDelete={canDelete ? setAutomationToDelete : undefined}
            />
            <ManageableItemsList.PaginationBar
              type="custom"
              currentPage={pageNumber}
              pageSize={AUTOMATIONS_PAGE_SIZE}
              totalItems={automations?.totalCount ?? 0}
              onPageChange={setPageNumber}
            />
          </>
        )}
      </ManageableItemsList>

      <Modal
        isOpen={!!automationToDelete}
        onClose={() => setAutomationToDelete(undefined)}
        title="Delete automation"
      >
        <Modal.Body>
          Are you sure you want to delete this automation?
        </Modal.Body>
        <Modal.Footer>
          <Modal.Footer.Actions>
            <Button
              variant="secondary"
              onClick={() => setAutomationToDelete(undefined)}
            >
              Cancel
            </Button>
            <Button
              colour="negative"
              isProcessing={isDeleting}
              onClick={() => {
                if (isDeleting || !automationToDelete) return;
                deleteAutomation(automationToDelete.id);
              }}
            >
              Delete
            </Button>
          </Modal.Footer.Actions>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default AutomationsTab;

const CreateAutomationButton: FC = () => {
  const history = useHistory();
  const { mutate: createAutomation, isPending } = useCreateAutomation({
    onSuccess: (data) =>
      history.push(getEditAutomationPath(data.createAutomation.privateId)),
  });

  return (
    <PermissionGuard
      renderIfHasPermissions={[AutomationsPermissions.ADD_AUTOMATION]}
    >
      <Button
        size="sm"
        isProcessing={isPending}
        onClick={() =>
          createAutomation({
            title: 'Untitled automation',
            isActive: false,
            entityType: 'QUOTE',
            triggerEvent: 'ACCEPTED',
            actions: [],
          })
        }
      >
        Create automation
      </Button>
    </PermissionGuard>
  );
};

type AutomationsTableProps = {
  automations: AutomationRow[];
  isLoading: boolean;
  isFiltered: boolean;
  sortBy: AutomationsSortableField;
  sortDirection: SortDirection;
  onSort: (field: AutomationsSortableField, direction: SortDirection) => void;
  onClickRow: (row: AutomationRow) => void;
  onDelete?: (row: AutomationRow) => void;
};

const AutomationsTable: FC<AutomationsTableProps> = (props) => {
  const {
    automations,
    isLoading,
    isFiltered,
    sortBy,
    sortDirection,
    onSort,
    onClickRow,
    onDelete,
  } = props;
  const { getTriggerDescriptionForAutomation } = useAutomationDescriptions();
  const preferredDateFormat = usePreferredDateFormat(DateFormats.MID);

  return (
    <ManageableItemsList.Table
      uniqueKey="id"
      items={automations}
      isLoading={isLoading}
      sortBy={sortBy}
      sortDirection={sortDirection}
      onClickRow={onClickRow}
      emptyText={
        isFiltered
          ? 'No automations match your search or filters'
          : 'No automations to show'
      }
      itemActions={
        onDelete ? [{ label: 'Delete', onClick: onDelete }] : undefined
      }
    >
      <ManageableItemsList.Table.Column<AutomationRow, 'title'>
        header="Name"
        field="title"
        className="w-full min-w-48 max-w-0 truncate"
        onSort={(_, direction) => onSort('title', direction)}
        render={(title, row) => (
          <>
            <TruncatedText fullText={title}>
              <strong>{title}</strong>
            </TruncatedText>
            <AutomationSummary
              trigger={getTriggerDescriptionForAutomation(
                row.entityType,
                row.triggerEvent
              )}
              actions={row.actions}
            />
          </>
        )}
      />
      <ManageableItemsList.Table.Column<AutomationRow, 'updatedAt'>
        header="Last updated"
        field="updatedAt"
        className="whitespace-nowrap"
        onSort={(_, direction) => onSort('updatedAt', direction)}
        render={(updatedAt) =>
          updatedAt ? format(updatedAt, preferredDateFormat) : '-'
        }
      />
      <ManageableItemsList.Table.Column<AutomationRow, 'isActive'>
        header="On/Off"
        field="isActive"
        render={(isActive) =>
          isActive ? (
            <Badge variant="soft" colour="green">
              <UntitledIcon name="check" className="w-4 h-4" />
              Active
            </Badge>
          ) : (
            <Badge variant="soft">Inactive</Badge>
          )
        }
      />
    </ManageableItemsList.Table>
  );
};

type TruncatedTextProps = PropsWithChildren<{
  fullText: string;
  isAbbreviated?: boolean;
}>;

const TruncatedText: FC<TruncatedTextProps> = (props) => {
  const { fullText, isAbbreviated = false, children } = props;
  const [isOverflowing, setIsOverflowing] = useState(false);

  return (
    <Tooltip
      tooltipContent={fullText}
      enabled={isAbbreviated || isOverflowing}
      className="block truncate"
      onMouseEnter={(event) =>
        setIsOverflowing(
          event.currentTarget.scrollWidth > event.currentTarget.clientWidth
        )
      }
    >
      {children}
    </Tooltip>
  );
};

type AutomationSummaryProps = {
  trigger: string;
  actions: AutomationRow['actions'];
};

const AutomationSummary: FC<AutomationSummaryProps> = (props) => {
  const { trigger, actions } = props;
  const translate = useTranslation();

  const names = [
    ...new Set(
      actions.map((action) =>
        action.__typename === 'UnsupportedAutomationAction'
          ? translate('automations.actionsSummary.unsupported')
          : getActionTypeLabel(action.__typename)
      )
    ),
  ];

  const actionsText = names.length
    ? summariseNames(translate, names)
    : translate('automations.actionsSummary.none');
  const allActionsText = names.length ? names.join(', ') : actionsText;

  return (
    <TruncatedText
      fullText={`${trigger} → ${allActionsText}`}
      isAbbreviated={names.length > 2}
    >
      <Text as="span" level="supporting" aria-hidden>
        {trigger} → {actionsText}
      </Text>
      <span className="sr-only">
        {translate('automations.summary', {
          trigger,
          actions: allActionsText,
        })}
      </span>
    </TruncatedText>
  );
};

const summariseNames = (
  translate: ReturnType<typeof useTranslation>,
  names: string[]
) => {
  const summary = names.slice(0, 2);
  if (names.length > 2) {
    summary.push(
      translate('automations.actionsSummary.more', {
        count: names.length - 2,
      })
    );
  }
  return summary.join(', ');
};
